import { useContext, useMemo } from "react";
import { PermissionContext } from "../utils/context/PermissionContext";
import { AccessLevelEnum } from "@propertelligent/client-api";

/**
 * Hook to check user permissions
 */
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }

  const { permissions, refreshPermissions } = context;

  const check = useMemo(() => {
    return {
      /**
       * Get current level for a specific permission
       */
      getLevel: (permissionName: string): number => {
        return permissions[permissionName] ?? AccessLevelEnum.None;
      },

      /**
       * Check if user has at least the required level
       */
      hasAccess: (permissionName: string, requiredLevel: number = AccessLevelEnum.ReadOnly): boolean => {
        const currentLevel = permissions[permissionName] ?? AccessLevelEnum.None;
        return currentLevel >= requiredLevel;
      },

      /**
       * Check if user has ALL access (level 2)
       */
      canWrite: (permissionName: string): boolean => {
        return (permissions[permissionName] ?? AccessLevelEnum.None) === AccessLevelEnum.All;
      },

      /**
       * Check if user has at least READ access (level 1 or 2)
       */
      canRead: (permissionName: string): boolean => {
        return (permissions[permissionName] ?? AccessLevelEnum.None) >= AccessLevelEnum.ReadOnly;
      },

      /**
       * Refresh permissions from sessionStorage
       */
      refreshPermissions,
    };
  }, [permissions, refreshPermissions]);

  return check;
};
